// frontend/src/types/httmSchedule.ts

export interface HttmConfig {
  startDate: string; // YYYY-MM-DD
  startTime: string; // HH:mm (24h format)
  initialH2o2Bath: boolean;
  includeH2o2: boolean; // Whether to include H2O2 baths in schedule
  includePrazi: boolean; // Whether to include PraziPro dosing in schedule
  qtTankSizeLiters: number; // QT tank size in liters
  bathVolumeLiters: number; // H2O2 bath container volume in liters
  h2o2Concentration: 3 | 6; // H2O2 percentage
  units: 'liters' | 'gallons'; // Display unit preference
  checkedItems: number; // Bitmask for checklist state
}

export type HttmCategory = 'h2o2' | 'transfer' | 'prazi' | 'observation';

export interface HttmEvent {
  day: number;
  date: Date;
  action: string;
  timing: string; // e.g., "After 48 hours", "Before 72 hours"
  notes: string;
  category: HttmCategory;
  tank: string; // Current location after this action
  skipped: boolean; // True if conditional and condition not met
}

// ── Dose calculation helpers ──

const LITERS_PER_GALLON = 3.78541;

/** PraziPro: 1 tsp (5ml) per 20 US gallons */
const PRAZIPRO_ML_PER_20GAL = 5;
const PRAZIPRO_LITERS_PER_DOSE_UNIT = 20 * LITERS_PER_GALLON; // ~75.7L

/** H2O2: 15ml of 6% per 6L is the baseline (2.5ml of 6% per liter) */
const H2O2_ML_6PCT_PER_LITER = 2.5;

export function calculateH2o2Dose(
  bathVolumeLiters: number,
  concentration: 3 | 6
): number {
  // Scale: 2.5ml of 6% per liter → adjust for actual concentration
  return bathVolumeLiters * H2O2_ML_6PCT_PER_LITER * (6 / concentration);
}

export function calculatePraziproDose(tankSizeLiters: number): number {
  return (tankSizeLiters / PRAZIPRO_LITERS_PER_DOSE_UNIT) * PRAZIPRO_ML_PER_20GAL;
}

export function litersToGallons(liters: number): number {
  return liters / LITERS_PER_GALLON;
}

export function gallonsToLiters(gallons: number): number {
  return gallons * LITERS_PER_GALLON;
}

export function formatVolume(
  liters: number,
  units: 'liters' | 'gallons',
  decimals = 1
): string {
  if (units === 'gallons') {
    return `${litersToGallons(liters).toFixed(decimals)} gal`;
  }
  return `${liters.toFixed(decimals)} L`;
}

// ── Airstone count ──

/**
 * 5 tank setups (Day 1, 4, 7, 10, 13).
 * No airstone needed for H2O2 baths (only 30 minutes).
 */
export const AIRSTONE_COUNT = 5;

// ── Equipment checklist ──

export interface ChecklistItem {
  id: string;
  label: string;
  group: string;
}

export const CHECKLIST_ITEMS: ChecklistItem[] = [
  // Tanks & Hardware
  { id: 'qt-tanks', label: '2 quarantine tanks', group: 'Tanks & Hardware' },
  { id: 'heaters', label: '2 heaters (one per tank)', group: 'Tanks & Hardware' },
  { id: 'air-pump', label: '1 air pump', group: 'Tanks & Hardware' },
  { id: 'airline', label: 'Airline tubing (for 2 tanks + bath container)', group: 'Tanks & Hardware' },
  { id: 'airstones', label: 'Airstones (disposable)', group: 'Tanks & Hardware' },
  { id: 'bath-container', label: 'H2O2 bath container', group: 'Tanks & Hardware' },
  { id: 'fish-hides', label: 'Fish hides (PVC fittings, etc.)', group: 'Tanks & Hardware' },
  { id: 'nets', label: '2 nets', group: 'Tanks & Hardware' },
  // Medications & Chemicals
  { id: 'h2o2', label: 'Hydrogen Peroxide', group: 'Medications & Chemicals' },
  { id: 'prazipro', label: 'PraziPro', group: 'Medications & Chemicals' },
  { id: 'cleaning', label: 'White vinegar or bleach (for cleaning between transfers)', group: 'Medications & Chemicals' },
  // Monitoring
  { id: 'ammonia', label: 'Seachem Ammonia Alert x2 (one per tank)', group: 'Monitoring' },
  { id: 'saltwater', label: 'Saltwater (pre-mixed, matched to DT params)', group: 'Monitoring' },
];
